import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crosshair, Loader2, Save, ArrowLeft, AlertTriangle } from "lucide-react";
import { Screen } from "@/components/kit";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { getPosition } from "@/lib/geo";
import { siteFromPermit } from "@/lib/ust";
import { FacilityCard, facilityTone } from "@/components/ust/FacilityCard";
import { TankList } from "@/components/ust/TankList";
import { UstSearch } from "@/components/ust/UstSearch";
import { RecentLookups, pushRecent } from "@/components/ust/RecentLookups";

export default function UstLookupPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [facs, setFacs] = useState(null);
  const [fac, setFac] = useState(null);
  const [tanks, setTanks] = useState(null);

  useEffect(() => {
    const saved = sessionStorage.getItem("fhp-ust-pick");
    if (!saved || user === null) return;
    sessionStorage.removeItem("fhp-ust-pick");
    open(JSON.parse(saved));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const find = async (radius = 250) => {
    setBusy(true); setMsg(null); setFac(null);
    const pos = await getPosition(10000);
    if (!pos) { setBusy(false); return setMsg({ tone: "danger", text: t("Couldn't get your location. Turn on location for this app and try again.") }); }
    try {
      const list = await api.ustNearby(pos.lat, pos.lng, radius);
      setFacs(list);
      if (!list.length) setMsg({ tone: "caution", text: radius < 1000 ? t("No registered tanks right here. Tap Search wider.") : t("No registered tanks found nearby. This store may not be in the EPA list.") });
    } catch (e) { setMsg({ tone: "danger", text: apiError(e) }); }
    setBusy(false);
  };
  const search = async (q, place) => {
    setBusy(true); setMsg(null); setFac(null);
    try {
      const list = await api.ustSearch(q, place);
      setFacs(list);
      if (!list.length) setMsg({ tone: "caution", text: t("No stores found. Check the spelling, try just the city or ZIP, or fewer words.") });
      else if (list.length >= 40) setMsg({ tone: "caution", text: t("Lots of matches. Add the store name or street to narrow it down.") });
    } catch (e) { setMsg({ tone: "danger", text: apiError(e) }); }
    setBusy(false);
  };
  const open = async (f) => {
    setFac(f); setTanks(null); setMsg(null); pushRecent(f);
    try { setTanks((await api.ustFacility(f.facilityId)).tanks); } catch (e) { setMsg({ tone: "danger", text: apiError(e) }); }
  };
  const save = async () => {
    if (!user) { sessionStorage.setItem("fhp-ust-pick", JSON.stringify(fac)); return nav("/login?next=/ust"); }
    const site = siteFromPermit(fac, tanks);
    if (!site.tanks.length) return setMsg({ tone: "danger", text: t("No open tanks with a known size to save.") });
    setBusy(true);
    try { await api.addSite(site); nav("/sites"); }
    catch (e) { setMsg({ tone: "danger", text: apiError(e) }); setBusy(false); }
  };

  const off = tanks?.filter((x) => !x.isOpen) || [];
  const usable = tanks?.filter((x) => x.isOpen && x.capacity) || [];
  return (
    <Screen testID="ust-screen" title={t("Store tank lookup")} subtitle={t("From the EPA's public tank permit list")}>
      {!fac && (<>
        <button type="button" className="primary-btn" onClick={() => find()} disabled={busy} data-testid="ust-find-btn">
          {busy ? <Loader2 size={18} className="spin" /> : <Crosshair size={18} />}{t("Find the store I'm at")}
        </button>
        {facs && <button type="button" className="ghost-btn" onClick={() => find(1200)} disabled={busy} data-testid="ust-wider-btn">{t("Search wider (¾ mile)")}</button>}
        <UstSearch busy={busy} onSearch={search} t={t} />
        {!facs && <RecentLookups onOpen={open} t={t} />}
      </>)}
      {msg && <div className={`scan-message tone-${msg.tone}`} data-testid="ust-message">{msg.text}</div>}
      {!fac && facs?.map((f) => <FacilityCard key={f.facilityId} f={f} onOpen={open} t={t} />)}
      {fac && (
        <div className="card ust-detail" data-testid="ust-detail">
          <button type="button" className="ghost-btn ust-back" onClick={() => setFac(null)} data-testid="ust-back-btn"><ArrowLeft size={16} />{t("Back to the list")}</button>
          <div className="card-name" data-testid="ust-detail-name">{fac.name}</div>
          <div className="card-sub">{fac.address}{fac.lastInspection ? ` · ${t("last inspected")} ${fac.lastInspection}` : ""}</div>
          {facilityTone(fac) === "danger" && <div className="scan-message tone-danger" data-testid="ust-out-of-service"><AlertTriangle size={16} /> {t("No open tanks on file. This store may be out of service — call dispatch before you load for it.")}</div>}
          {tanks === null ? <div className="card-sub">…</div> : <TankList tanks={tanks} t={t} />}
          {off.length > 0 && usable.length > 0 && <div className="scan-message tone-caution" data-testid="ust-off-note">{t("Closed or out of service, not saved: {n}. Never drop into a tank marked out of service.", { n: off.length })}</div>}
          {usable.length > 0 && (<>
            <p className="scan-why">{t("Permits list gallons only, not width. The app uses the most common width for each size — scan a Veeder slip or add the store's chart to make it exact.")}</p>
            <button type="button" className="primary-btn" onClick={save} disabled={busy} data-testid="ust-save-btn">
              <Save size={18} />{user ? t("Save store as a station ({n} tanks)", { n: usable.length }) : t("Sign in to save this store")}
            </button>
          </>)}
        </div>
      )}
      <p className="scan-caution">{t("EPA data comes from each state and can be out of date. Always check the store's tanks and fill caps yourself.")}</p>
    </Screen>
  );
}
